"use client";

import { Megaphone, X } from "lucide-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "mint-glm52-announcement-dismissed";

export interface MintGlmAnnouncementCopy {
  label: string;
  closeLabel: string;
}

interface MintGlmAnnouncementProps {
  copy: MintGlmAnnouncementCopy;
}

export default function MintGlmAnnouncement({ copy }: MintGlmAnnouncementProps) {
  const [visible, setVisible] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const textRef = useRef<HTMLSpanElement>(null);
  const textInnerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setVisible(sessionStorage.getItem(STORAGE_KEY) !== "1");
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const text = textRef.current;
    const textInner = textInnerRef.current;
    if (!text || !textInner) {
      return;
    }

    const updateScrollDistance = () => {
      setScrollDistance(textInner.scrollWidth > text.clientWidth ? textInner.scrollWidth : 0);
    };

    updateScrollDistance();

    const resizeObserver = new ResizeObserver(updateScrollDistance);
    resizeObserver.observe(text);
    resizeObserver.observe(textInner);
    window.addEventListener("resize", updateScrollDistance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollDistance);
    };
  }, [copy.label, visible]);

  if (!visible) {
    return null;
  }

  return (
    <aside className="glm-announcement" aria-label={copy.label}>
      <Megaphone className="glm-announcement-icon" aria-hidden="true" />
      <span
        className={`glm-announcement-text${scrollDistance > 0 ? " is-scrollable" : ""}`}
        ref={textRef}
        style={{ "--glm-announcement-scroll": `-${scrollDistance}px` } as CSSProperties}
      >
        <span className="glm-announcement-text-track">
          <span className="glm-announcement-text-inner" ref={textInnerRef}>
            {copy.label}
          </span>
          {scrollDistance > 0 ? (
            <span className="glm-announcement-text-inner" aria-hidden="true">
              {copy.label}
            </span>
          ) : null}
        </span>
      </span>
      <button
        className="glm-announcement-close"
        type="button"
        aria-label={copy.closeLabel}
        onClick={() => {
          sessionStorage.setItem(STORAGE_KEY, "1");
          setVisible(false);
        }}
      >
        <X aria-hidden="true" />
      </button>
    </aside>
  );
}
